<template>
  <!-- Stacked: the brand leads, GrainFox is credited underneath. For hero areas. -->
  <div
    v-if="stacked"
    class="cobranded cobranded--stacked d-flex flex-column"
    :class="{'align-center': center, 'align-start': !center}"
  >
    <img
      :src="dark ? brandLogoWhite : brandLogo"
      :alt="brandName"
      :height="height"
      class="cobranded__logo"
    >
    <div class="cobranded__credit d-flex align-center justify-end">
      <span
        class="cobranded__caption text-overline"
        :class="dark ? 'white--text' : 'primary-grey--text'"
      >
        POWERED BY
      </span>
      <img
        :src="dark ? grainFoxLogoWhiteSVG : grainFoxLogoSVG"
        alt="GrainFox"
        :height="creditHeight"
        class="cobranded__logo"
      >
    </div>
  </div>

  <!-- Side by side, equal height: neither mark outranks the other. -->
  <div
    v-else
    class="cobranded d-flex align-center"
    :class="{'justify-center': center}"
  >
    <img
      :src="dark ? brandLogoWhite : brandLogo"
      :alt="brandName"
      :height="height"
      class="cobranded__logo"
    >
    <span
      class="cobranded__rule"
      :class="dark ? 'cobranded__rule--light' : 'cobranded__rule--dark'"
      :style="ruleStyle"
    />
    <img
      :src="dark ? grainFoxLogoWhiteSVG : grainFoxLogoSVG"
      alt="GrainFox"
      :height="height"
      class="cobranded__logo"
    >
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import grainFoxLogoSVG from '@/assets/images/grainfox-logo-black.svg?url';
import grainFoxLogoWhiteSVG from '@/assets/images/grainfox-logo-white.svg?url';

/**
 * A branded app's logo next to the GrainFox one, separated by a rule.
 *
 * Both sit at the same height so neither reads as the primary mark, which is the point of
 * co-branding. The rule is deliberately shorter than the logos - a full-height divider
 * makes the pair look like two unrelated blocks.
 *
 * Renders nothing when no branded app is in play, so callers need no v-if of their own.
 */
export default {
  name: 'CoBrandedLogos',

  props: {
    // Height of both logos in pixels. 32 suits page headers, 26 a dialog.
    height: {
      type: Number,
      default: 32,
    },
    // Use the white logos, for dark surfaces such as the marketing panel.
    dark: {
      type: Boolean,
      default: false,
    },
    center: {
      type: Boolean,
      default: false,
    },
    // Put GrainFox below the brand instead of beside it, for hero areas where the brand
    // should lead and there is room for a credit line.
    stacked: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      grainFoxLogoSVG,
      grainFoxLogoWhiteSVG,
    };
  },

  computed: {
    ...mapGetters('app', ['brandLogo', 'brandLogoWhite', 'brandName']),

    ruleStyle() {
      // Shorter than the logos, and never taller than a thin hairline is meant to be.
      return `height: ${Math.round(this.height * 0.8)}px;`;
    },

    // The credit reads as secondary, so GrainFox sits well below the brand's size.
    creditHeight() {
      return Math.max(16, Math.round(this.height * 0.55));
    },
  },
};
</script>

<style lang="scss" scoped>
.cobranded {
  // Keeps the pair from crowding whatever sits above or below it.
  gap: 16px;

  &--stacked {
    // Tighter than the space above the block: the credit reads as part of the logo,
    // not as a third line of its own.
    gap: 5px;
    // Only as wide as the brand logo, so the credit line below can end flush with it.
    width: fit-content;
  }

  &__credit {
    gap: 8px;
    width: 100%;
  }

  &__caption {
    // The overline class is roomy by default; this keeps the credit compact.
    font-size: 10px !important;
    letter-spacing: 1.6px !important;
    line-height: 1 !important;
    opacity: 0.85;
  }

  &__logo {
    display: block;
    object-fit: contain;
    // A wordmark must never be squeezed by a narrow parent.
    max-width: 100%;
  }

  &__rule {
    flex: 0 0 auto;
    width: 1px;

    &--dark {
      background-color: rgba(0, 0, 0, 0.22);
    }

    &--light {
      background-color: rgba(255, 255, 255, 0.45);
    }
  }
}
</style>
